import { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Receipt, MagnifyingGlass, Gear, Trash, Archive } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canEditShizai } from '@/auth/AuthContext';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { shizaiApi, ORDER_STATUS_BADGE, ORDER_STATUSES, ORDER_TYPE_BADGE, APPROVAL_BADGE, yen, num, packOf } from './shizaiApi';
import { DataTable } from '@/components/ui/DataTable';
import OrderFormDialog from './OrderFormDialog';
import OrderDetailDialog from './OrderDetailDialog';

export default function Orders() {
  const { t } = useT();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = canEditShizai(user);
  const [list, setList] = useState([]);
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [approvalOnly, setApprovalOnly] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const [prefillRequest, setPrefillRequest] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (status !== 'all') params.status = status;
    if (q.trim()) params.q = q.trim();
    if (approvalOnly) params.approval_status = 'pending';
    if (showArchived) params.archived = true;
    shizaiApi.orders(params).then(setList).catch(() => {});
  }, [status, q, approvalOnly, showArchived]);
  useEffect(() => { const id = setTimeout(load, 200); return () => clearTimeout(id); }, [load]);

  // Handle navigation state from requests/dashboard (place order / open detail).
  useEffect(() => {
    const st = location.state;
    if (st?.request) {
      const seed = st.request;
      // Always refresh from backend so we have the freshest lines[] with
      // per-line supplier_id + line_status (multi-supplier partial-order flow).
      if (seed.id) {
        shizaiApi.request(seed.id)
          .then((full) => { setPrefillRequest(full); setEditOrder(null); setFormOpen(true); })
          .catch(() => { setPrefillRequest(seed); setEditOrder(null); setFormOpen(true); });
      } else {
        setPrefillRequest(seed); setEditOrder(null); setFormOpen(true);
      }
      navigate(location.pathname, { replace: true, state: {} });
    } else if (st?.openOrderId) {
      setDetailId(st.openOrderId);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const openNew = () => { setEditOrder(null); setPrefillRequest(null); setFormOpen(true); };
  const lineSummary = (o) => {
    const first = o.lines?.[0];
    if (!first) return '—';
    const extra = o.lines.length > 1 ? ` 他${o.lines.length - 1}件` : '';
    return `${first.name || first.part_no || '—'}${extra}`;
  };
  const totalQty = (o) => {
    const groups = {};
    (o.lines || []).forEach((l) => {
      const pk = packOf(l);
      const u = pk ? pk.packUom : (l.uom || '個');
      groups[u] = (groups[u] || 0) + (pk ? pk.packs : (Number(l.qty) || 0));
    });
    const parts = Object.entries(groups).map(([u, n]) => `${num(n)}${u}`);
    return parts.length ? parts.join(' / ') : '0';
  };

  // ── bulk select / delete / archive (data-prep for real-data go-live) ──
  const allSelected = list.length > 0 && list.every((o) => selected.has(o.id));
  const someSelected = list.some((o) => selected.has(o.id));
  const toggleSelect = (id, on) => setSelected((p) => { const n = new Set(p); if (on) n.add(id); else n.delete(id); return n; });
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(list.map((o) => o.id)));

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!window.confirm(`選択した ${ids.length}件の発注を削除しますか？\n\n・紐づく発注依頼の明細は「未発注」に戻ります\n・入荷実績がある発注は削除されずスキップされます\n・この操作は元に戻せません`)) return;
    setBulkBusy(true);
    try {
      const res = await shizaiApi.bulkDeleteOrders(ids);
      if (res.skipped?.length) {
        toast.warning(`${res.deleted}件削除 / ${res.skipped.length}件スキップ（${res.skipped[0].reason}${res.skipped.length > 1 ? ' 他' : ''}）`);
      } else {
        toast.success(`${res.deleted}件の発注を削除しました`);
      }
      setSelected(new Set()); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('shizai.error')); }
    finally { setBulkBusy(false); }
  };

  const bulkArchive = async () => {
    const ids = Array.from(selected);
    const toArchived = !showArchived;
    if (!window.confirm(toArchived
      ? `選択した ${ids.length}件の発注をアーカイブしますか？\n（一覧から非表示になりますが、データは保持されます）`
      : `選択した ${ids.length}件の発注をアーカイブ解除しますか？`)) return;
    setBulkBusy(true);
    try {
      const res = await shizaiApi.bulkArchiveOrders(ids, toArchived);
      toast.success(`${res.updated}件を${toArchived ? 'アーカイブしました' : 'アーカイブ解除しました'}`);
      setSelected(new Set()); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('shizai.error')); }
    finally { setBulkBusy(false); }
  };

  const selectColumn = canWrite ? {
    key: 'select',
    header: (
      <input
        type="checkbox"
        data-testid="orders-select-all"
        checked={allSelected}
        ref={(el) => { if (el) el.indeterminate = !allSelected && someSelected; }}
        onChange={toggleSelectAll}
        onClick={(e) => e.stopPropagation()}
        className="h-4 w-4 rounded border-zinc-300 accent-rose-500 cursor-pointer align-middle"
        title="全て選択 / 解除"
      />
    ),
    width: 'w-10',
    sortable: false,
    mobileBadge: true,
    cell: (o) => (
      <input
        type="checkbox"
        data-testid={`order-select-${o.id}`}
        checked={selected.has(o.id)}
        onChange={(e) => toggleSelect(o.id, e.target.checked)}
        onClick={(e) => e.stopPropagation()}
        className="h-4 w-4 rounded border-zinc-300 accent-rose-500 cursor-pointer"
      />
    ),
  } : null;

  return (
    <div className="space-y-5 page-enter" data-testid="shizai-orders">
      <PageHeader
        testid="orders-header"
        title="発注一覧"
        description="発注書の作成・送付・入荷状況を管理します"
        menuItems={canWrite ? [
          { label: t('shizai.approval.settings'), icon: <Gear size={16} weight="duotone" />, onClick: () => navigate('/shizai/settings'), testid: 'order-config-btn' },
        ] : undefined}
        primary={canWrite ? (
          <Button data-testid="order-new-btn" onClick={openNew} className="h-10">
            <Plus size={17} className="mr-1.5" weight="bold" /> {t('shizai.order.new')}
          </Button>
        ) : null}
      />

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1.5 flex-wrap">
          {['all', ...ORDER_STATUSES].map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`order-filter-${s}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                status === s ? 'bg-teal-700 text-white border-teal-700' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}>
              {s === 'all' ? t('shizai.all') : t(`shizai.orderStatus.${s}`)}
            </button>
          ))}
        </div>
        <div className="relative max-w-xs flex-1 min-w-[180px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input data-testid="order-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('shizai.search')} className="h-10 pl-9 rounded-lg" />
        </div>
        <button onClick={() => setApprovalOnly((v) => !v)} data-testid="order-approval-filter"
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${approvalOnly ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-200 hover:bg-amber-50'}`}>
          {t('shizai.approval.pendingOnly')}
        </button>
        <button onClick={() => { setShowArchived((v) => !v); setSelected(new Set()); }} data-testid="order-archived-filter"
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors inline-flex items-center gap-1 ${showArchived ? 'bg-zinc-600 text-white border-zinc-600' : 'bg-white text-zinc-500 border-zinc-200 hover:bg-zinc-50'}`}>
          <Archive size={12} weight="bold" />
          アーカイブ
        </button>
      </div>

      {canWrite && selected.size > 0 && (
        <div className="bg-zinc-50 border border-zinc-200 rounded-lg px-4 py-3 flex items-center gap-3 flex-wrap shadow-sm sticky top-2 z-10" data-testid="orders-bulk-bar">
          <span className="text-sm font-medium text-zinc-900">選択中 {selected.size}件</span>
          <button onClick={() => setSelected(new Set())} data-testid="orders-bulk-clear"
            className="text-xs text-rose-700 underline hover:text-rose-900">選択解除</button>
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={bulkArchive} disabled={bulkBusy} data-testid="orders-bulk-archive-btn"
              className="h-9 border-zinc-300 text-zinc-700 hover:bg-zinc-100">
              <Archive size={14} className="mr-1.5" />
              {showArchived ? 'アーカイブ解除' : 'アーカイブ'} ({selected.size})
            </Button>
            <Button size="sm" onClick={bulkDelete} disabled={bulkBusy} data-testid="orders-bulk-delete-btn"
              className="h-9 bg-rose-600 hover:bg-rose-700 text-white">
              <Trash size={14} className="mr-1.5" />
              削除 ({selected.size})
            </Button>
          </div>
        </div>
      )}

      {/* minWidth: 10列 = 固定幅の合計760px。1100px 未満では flex 列（仕入先・明細）が
          読めない幅まで潰れるので、潰さず横スクロールさせる。 */}
      <DataTable
        dataTestId="order-table"
        rows={list}
        minWidth="min-w-[1100px]"
        rowKey={(o) => o.id}
        rowTestId={(o) => `order-row-${o.id}`}
        onRowClick={(o) => setDetailId(o.id)}
        empty={{ icon: <Receipt size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />, text: t('shizai.order.empty'), testid: 'order-empty' }}
        columns={[
          selectColumn,
          { key: 'order_no', primary: true, header: t('shizai.order.no'), width: 'w-32', mono: true, cell: (o) => <span className="font-mono text-xs text-zinc-700 whitespace-nowrap">{o.order_no}{o.order_type === 'subcontract' && <Badge variant="outline" className={`ml-1.5 ${ORDER_TYPE_BADGE.subcontract}`} data-testid={`order-type-${o.id}`}>{t('shizai.orderTypeShort.subcontract')}</Badge>}</span> },
          { key: 'supplier', header: t('shizai.order.supplier'), flex: true, cell: (o) => <span className="min-w-0 font-medium text-zinc-800 line-clamp-2 break-words" title={o.supplier?.name || ''}>{o.supplier?.name || '—'}</span> },
          { key: 'buyer', header: '発注者', width: 'w-24', cell: (o) => <span className="text-zinc-600 text-xs whitespace-nowrap" data-testid={`order-buyer-${o.id}`}>{o.created_by || '—'}</span> },
          { key: 'lines', header: t('shizai.order.lines'), flex: true, cell: (o) => <span className="min-w-0 text-zinc-600 line-clamp-2 break-words" title={lineSummary(o)}>{lineSummary(o)}</span> },
          { key: 'qty', header: t('shizai.req.qty'), width: 'w-20', align: 'right', cell: (o) => <span className="tabular-nums text-zinc-700">{totalQty(o)}</span> },
          { key: 'total', header: t('shizai.order.total'), width: 'w-28', align: 'right', cell: (o) => <span className="tabular-nums font-medium text-zinc-800">{yen(o.total)}</span> },
          { key: 'due', header: t('shizai.order.dueDate'), width: 'w-24', cell: (o) => <span className={o.overdue ? 'text-rose-600 font-semibold whitespace-nowrap' : 'text-zinc-600 whitespace-nowrap'}>{o.due_date || '—'}</span> },
          { key: 'status', header: '', width: 'w-32', cell: (o) => (<div className="flex items-center gap-1.5 whitespace-nowrap"><Badge variant="outline" className={ORDER_STATUS_BADGE[o.status]}>{t(`shizai.orderStatus.${o.status}`)}</Badge>{o.approval_status && o.approval_status !== 'not_required' && <Badge variant="outline" className={APPROVAL_BADGE[o.approval_status]} data-testid={`order-approval-${o.id}`}>{t(`shizai.approval.${o.approval_status}`)}</Badge>}</div>) },
          { key: 'actions', actions: true, header: '', cell: (o) => <Button size="sm" variant="outline" className="h-9" data-testid={`order-view-${o.id}`} onClick={(e) => { e.stopPropagation(); setDetailId(o.id); }}>{t('shizai.view')}</Button> },
        ]}
      />

      <OrderFormDialog
        open={formOpen}
        order={editOrder}
        prefillRequest={prefillRequest}
        onClose={() => { setFormOpen(false); setEditOrder(null); setPrefillRequest(null); }}
        onSaved={(o) => { setFormOpen(false); setEditOrder(null); setPrefillRequest(null); load(); if (o?.id) setDetailId(o.id); }}
      />
      <OrderDetailDialog
        orderId={detailId}
        onClose={() => setDetailId(null)}
        onChanged={load}
        onEdit={(o) => { setDetailId(null); setEditOrder(o); setPrefillRequest(null); setFormOpen(true); }}
      />
    </div>
  );
}
